import { useCallback, useEffect, useRef, useState } from 'react'

// Shared page-data loader: tracks loading/error, guards against out-of-order
// responses (rapid filter changes, refresh spam) with a run counter so only the
// latest request's result lands, and cancels on unmount.
//
// `deps` is spread straight into an effect's dependency array, so every entry must
// be referentially stable across renders — pass state values, never an object or
// array literal, or the effect refires forever.
//
// Returns `loading` (true for ANY in-flight request, including refetches) and
// `hasLoaded` (false only until the first request settles). Use `hasLoaded` to
// decide between a full-page spinner and keeping stale content visible, and
// `loading` to signal that a refresh is in progress.
export function useAsyncData(fetcher, deps, { onError } = {}) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [hasLoaded, setHasLoaded] = useState(false)
  const [error, setError] = useState(null)
  const runIdRef = useRef(0)
  const fetcherRef = useRef(fetcher)
  fetcherRef.current = fetcher
  const onErrorRef = useRef(onError)
  onErrorRef.current = onError

  const reload = useCallback(async () => {
    const runId = ++runIdRef.current
    setLoading(true)
    try {
      const result = await fetcherRef.current()
      if (runId !== runIdRef.current) return
      setData(result)
      setError(null)
    } catch (err) {
      if (runId !== runIdRef.current) return
      setError(err)
      onErrorRef.current?.(err)
    } finally {
      // Settled either way — an error still counts as "we have tried once", so the
      // page can stop showing its first-load spinner.
      if (runId === runIdRef.current) {
        setLoading(false)
        setHasLoaded(true)
      }
    }
  }, [])

  useEffect(() => {
    reload()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)

  // Invalidate any in-flight request on unmount.
  useEffect(() => () => { runIdRef.current++ }, [])

  return { data, setData, loading, hasLoaded, error, reload }
}
